/**
 * DashboardPage.js - Dashboard Revolucionário CG Business ERP
 * Uma experiência única no mundo dos ERPs de vendas
 * 
 * Cores Corporativas:
 * - Laranja: #F6931F
 * - Azul: #2B4DAA
 * 
 * Features:
 * - Design premium com glassmorphism e gradientes modernos
 * - Widgets modulares e interactivos
 * - Animações fluidas e micro-interações
 * - Feed de actividade em tempo real
 * - Sistema de gamificação integrado
 * - Metas e performance visual
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsAPI, settingsAPI, servicesAPI, inventoryAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, ShoppingCart, Receipt,
  Wallet, AlertTriangle, Users, Package, MapPin, RefreshCw,
  ArrowUp, ArrowDown, Minus, BarChart3, PieChart, Activity,
  Clock, CheckCircle, XCircle, Calendar, Zap, Sparkles,
  Trophy, Target, Award, Star, Flame, Crown, Medal,
  FileText, Wrench, ArrowRight, Bell, Plus, Eye,
  ChevronRight, Percent, Building2, TrendingUpIcon,
  Banknote, CreditCard, Truck, Settings, LayoutDashboard
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart,
  RadialBarChart, RadialBar
} from 'recharts';
import { formatCurrency, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { PeriodSelectControl } from '@/components/common/PeriodSelectControl';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { buildMaputoRangeBoundary, buildTranslatedPeriodOptions, formatSelectedDateRangeLabel } from '@/lib/periodFilters';

// ============================================================================
// CONSTANTS - Corporate Colors
// ============================================================================

// Corporate Colors - CG Business
const COLORS = {
  primary: '#2B4DAA',     // Corporate Blue
  secondary: '#F6931F',   // Corporate Orange
  success: '#10b981',
  warning: '#F6931F',     // Use corporate orange for warnings
  danger: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899',
  cyan: '#06b6d4',
  lime: '#84cc16',
  blue: '#2B4DAA',        // Corporate Blue
  orange: '#F6931F'       // Corporate Orange
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.success, COLORS.purple, COLORS.pink, COLORS.cyan];

const ACTIVITY_TYPE_CONFIG = {
  sale: { icon: ShoppingCart, iconClass: 'text-emerald-600', bgClass: 'bg-emerald-100' },
  quotation: { icon: FileText, iconClass: 'text-blue-600', bgClass: 'bg-blue-100' },
  service: { icon: Wrench, iconClass: 'text-orange-600', bgClass: 'bg-orange-100' },
  client: { icon: Users, iconClass: 'text-purple-600', bgClass: 'bg-purple-100' },
  supplier: { icon: Building2, iconClass: 'text-indigo-600', bgClass: 'bg-indigo-100' },
  user: { icon: Users, iconClass: 'text-sky-600', bgClass: 'bg-sky-100' },
  stock: { icon: Package, iconClass: 'text-cyan-600', bgClass: 'bg-cyan-100' },
  payment: { icon: DollarSign, iconClass: 'text-emerald-700', bgClass: 'bg-emerald-100' },
  settings: { icon: Settings, iconClass: 'text-slate-700', bgClass: 'bg-slate-100' },
  alert: { icon: AlertTriangle, iconClass: 'text-red-600', bgClass: 'bg-red-100' }
};

const translateActivityValue = (t, key, fallback) => {
  const translated = t(key);
  return translated === key ? fallback : translated;
};

const normalizeActivityEntityKey = (entity, type) => {
  const normalized = String(entity || type || 'generic').trim().toLowerCase();

  if (['sale', 'sales'].includes(normalized)) return 'sale';
  if (normalized.includes('service')) return 'service';
  if (normalized.includes('client') || normalized.includes('customer')) return 'client';
  if (normalized.includes('supplier')) return 'supplier';
  if (normalized.includes('user') || normalized.includes('employee')) return 'user';
  if (normalized.includes('stock') || normalized.includes('product') || normalized.includes('inventory') || normalized.includes('item')) return 'stock';
  if (normalized.includes('expense')) return 'expense';
  if (normalized.includes('payment') || normalized.includes('cash')) return 'payment';
  if (normalized.includes('document') || normalized.includes('quotation') || normalized.includes('invoice')) return 'document';
  if (normalized.includes('setting') || normalized.includes('config')) return 'settings';

  return 'generic';
};

const normalizeActivityType = (type, entityKey) => {
  const normalizedType = String(type || '').trim().toLowerCase();

  if (ACTIVITY_TYPE_CONFIG[normalizedType]) {
    return normalizedType;
  }

  const fallbackMap = {
    document: 'quotation',
    expense: 'payment',
    payment: 'payment',
    settings: 'settings',
    generic: 'alert'
  };

  return fallbackMap[entityKey] || entityKey || 'alert';
};

const formatRelativeActivityTime = (timestamp, locale) => {
  if (!timestamp) return '';

  const target = new Date(timestamp);
  if (Number.isNaN(target.getTime())) {
    return '';
  }

  const diffMs = target.getTime() - Date.now();
  const diffSeconds = Math.round(diffMs / 1000);
  const thresholds = [
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];

  for (const [unit, secondsPerUnit] of thresholds) {
    if (Math.abs(diffSeconds) >= secondsPerUnit || unit === 'minute') {
      const value = Math.round(diffSeconds / secondsPerUnit);
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, unit);
    }
  }

  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(diffSeconds, 'second');
};

const getActivityTestId = (activityId) => `dashboard-activity-item-${String(activityId || 'unknown').replace(/[^a-zA-Z0-9-_]/g, '-')}`;

const getActivityReactKey = (activity) => {
  if (activity?.id) {
    return [activity.id, activity.timestamp, activity.action, activity.subject]
      .filter(Boolean)
      .join('-');
  }
  const composedKey = [activity?.timestamp, activity?.subject, activity?.action, activity?.type]
    .filter(Boolean)
    .join('-');
  return composedKey || 'dashboard-activity-unknown';
};

const formatDashboardDate = (value, locale) => {
  if (!value) return '—';
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '—';
  return parsedDate.toLocaleDateString(locale, { timeZone: 'Africa/Maputo' });
};

const ALERT_SOURCE_LABELS = {
  cash_management: 'Caixa',
  sales: 'Vendas',
  clients: 'Clientes',
  expenses: 'Despesas',
  inventory: 'Inventário',
  alerts: 'Alertas',
};

const ALERT_SEVERITY_CLASSES = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200',
};

// ============================================================================
// ANIMATED COMPONENTS
// ============================================================================


// Animated Number Counter
const AnimatedNumber = ({ value, duration = 1000, format = 'number', prefix = '', suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValueRef = useRef(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    const startValue = previousValueRef.current;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + (value - startValue) * easeOut;
      setDisplayValue(nextValue);
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = value;
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  
  const formatted = format === 'currency' 
    ? formatCurrency(displayValue)
    : format === 'percent'
    ? `${Math.round(displayValue)}%`
    : Math.round(displayValue).toLocaleString('pt-PT');
    
  return <span>{prefix}{formatted}{suffix}</span>;
};

// Live Pulse Indicator
const LivePulse = ({ label = "Ao Vivo" }) => (
  <div className="flex items-center gap-2">
    <span className="relative flex h-2.5 w-2.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
    </span>
    <span className="text-xs font-medium text-emerald-600">{label}</span>
  </div>
);

// Glassmorphic Card
const GlassCard = ({ children, className, gradient = false, ...props }) => (
  <div 
    className={cn(
      "rounded-2xl border border-white/20 backdrop-blur-xl",
      "bg-white/80 dark:bg-slate-900/80",
      "shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50",
      "transition-all duration-300 hover:shadow-2xl hover:scale-[1.01]",
      gradient && "bg-gradient-to-br from-white/90 to-slate-50/90",
      className
    )}
    {...props}
  >
    {children}
  </div>
);

// Stat Card with Trend
const StatCard = ({ 
  title, 
  value, 
  previousValue, 
  change, 
  icon: Icon, 
  color = "blue",
  format = "currency",
  subtitle,
  onClick,
  testId
}) => {
  const isPositive = change > 0;
  const isNegative = change < 0;
  
  const colorClasses = {
    blue: "from-blue-500 to-indigo-600",
    green: "from-emerald-500 to-teal-600",
    purple: "from-purple-500 to-violet-600",
    orange: "from-orange-500 to-amber-600",
    red: "from-red-500 to-rose-600",
    pink: "from-pink-500 to-rose-600",
    cyan: "from-cyan-500 to-blue-600"
  };
  
  return (
    <GlassCard 
      className={cn("p-5 cursor-pointer group", onClick && "hover:border-blue-300")}
      onClick={onClick}
      data-testid={testId || `stat-card-${String(title || 'stat').toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <div className="text-2xl font-bold text-slate-900">
            <AnimatedNumber value={value} format={format} duration={1200} />
          </div>
          
          {change !== undefined && change !== null && (
            <div className="flex items-center gap-1.5 mt-2">
              <div className={cn(
                "flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium",
                isPositive && "bg-emerald-100 text-emerald-700",
                isNegative && "bg-red-100 text-red-700",
                !isPositive && !isNegative && "bg-slate-100 text-slate-600"
              )}>
                {isPositive && <ArrowUp className="h-3 w-3" />}
                {isNegative && <ArrowDown className="h-3 w-3" />}
                {!isPositive && !isNegative && <Minus className="h-3 w-3" />}
                <span>{Math.abs(change).toFixed(1)}%</span>
              </div>
              {subtitle && (
                <span className="text-xs text-slate-400">{subtitle}</span>
              )}
            </div>
          )}
        </div>
        
        <div className={cn(
          "p-3 rounded-xl bg-gradient-to-br shadow-lg",
          "group-hover:scale-110 transition-transform duration-300",
          colorClasses[color]
        )}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </GlassCard>
  );
};

// Quick Action Button (Enhanced)
const QuickActionBtn = ({ icon: Icon, label, onClick, color = "blue", badge, testId }) => {
  const bgColors = {
    blue: "hover:bg-blue-50 hover:border-blue-200",
    green: "hover:bg-emerald-50 hover:border-emerald-200",
    purple: "hover:bg-purple-50 hover:border-purple-200",
    orange: "hover:bg-orange-50 hover:border-orange-200",
    pink: "hover:bg-pink-50 hover:border-pink-200"
  };
  
  const iconColors = {
    blue: "text-blue-600 group-hover:text-blue-700",
    green: "text-emerald-600 group-hover:text-emerald-700",
    purple: "text-purple-600 group-hover:text-purple-700",
    orange: "text-orange-600 group-hover:text-orange-700",
    pink: "text-pink-600 group-hover:text-pink-700"
  };
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "group flex items-center gap-3 w-full p-3 rounded-xl border border-slate-200",
        "bg-white transition-all duration-200",
        "hover:shadow-md active:scale-[0.98]",
        bgColors[color]
      )}
      data-testid={testId || `quick-action-${String(label || 'action').toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className={cn(
        "p-2 rounded-lg bg-slate-100 transition-colors",
        `group-hover:bg-${color}-100`
      )}>
        <Icon className={cn("h-5 w-5", iconColors[color])} />
      </div>
      <span className="font-medium text-slate-700 flex-1 text-left">{label}</span>
      {badge && (
        <Badge variant="secondary" className="text-xs">{badge}</Badge>
      )}
      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
    </button>
  );
};

// Activity Item
const ActivityItem = ({ activity }) => {
  const config = ACTIVITY_TYPE_CONFIG[activity.type] || ACTIVITY_TYPE_CONFIG.alert;
  const Icon = config.icon;
  
  return (
    <div
      className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
      data-testid={getActivityTestId(activity.id)}
    >
      <div className={cn('p-2 rounded-full', config.bgClass)}>
        <Icon className={cn('h-4 w-4', config.iconClass)} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 truncate">{activity.title}</p>
        <p className="text-xs text-slate-500 truncate">{activity.description}</p>
      </div>
      <div className="text-xs text-slate-400 whitespace-nowrap">{activity.time}</div>
    </div>
  );
};

// Mini Chart for Trend
const MiniTrendChart = ({ data, color = COLORS.primary }) => {
  if (!data || data.length === 0) return null;
  
  return (
    <ResponsiveContainer width="100%" height={60}>
      <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <defs>
          <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey="value" 
          stroke={color} 
          strokeWidth={2}
          fill={`url(#gradient-${color.replace('#', '')})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-slate-900 mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={`${entry.dataKey || entry.name || 'tooltip'}-${entry.color || 'default'}`} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, hasRole, defaultDocumentLocationId, defaultDocumentLocationName } = useAuth();
  const { t, tPeriod, language } = useLanguage();
  // Permitir acesso ao dashboard para mais perfis, com diferentes níveis de informação
  const canViewDashboard = hasRole(['admin', 'store_manager', 'supervisor', 'accountant', 'sales', 'cashier', 'staff']);
  const locale = language === 'en' ? 'en-GB' : 'pt-PT';
  
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('this_month');
  const [locationFilter, setLocationFilter] = useState(defaultDocumentLocationId || 'all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [locations, setLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data state
  const [summary, setSummary] = useState(null);
  const [salesChart, setSalesChart] = useState(null);
  const [expensesChart, setExpensesChart] = useState(null);
  const [topSales, setTopSales] = useState(null);
  const [dashboardAlerts, setDashboardAlerts] = useState({ items: [], by_source_module: {}, diagnostics: null });
  const [recentActivityRecords, setRecentActivityRecords] = useState([]);
  const [pendingTasks, setPendingTasks] = useState({});
  const [performanceData, setPerformanceData] = useState(null);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();

  const periodOptions = useMemo(() => buildTranslatedPeriodOptions(tPeriod), [tPeriod]);

  const selectedPeriodLabel = useMemo(() => {
    if (period === 'custom') {
      const customLabel = formatSelectedDateRangeLabel(customStartDate, customEndDate, locale);
      if (customLabel) return customLabel;
    }
    return periodOptions.find((option) => option.value === period)?.label || t('dashboard.selectedPeriod');
  }, [customEndDate, customStartDate, locale, period, periodOptions, t]);

  const dashboardParams = useMemo(() => {
    const params = { period };
    if (locationFilter && locationFilter !== 'all') params.location_id = locationFilter;
    if (period === 'custom') {
      params.custom_start = buildMaputoRangeBoundary(customStartDate, 'start');
      params.custom_end = buildMaputoRangeBoundary(customEndDate, 'end');
    }
    return params;
  }, [customEndDate, customStartDate, locationFilter, period]);

  useEffect(() => {
    if (defaultDocumentLocationId && locationFilter === 'all') {
      setLocationFilter(defaultDocumentLocationId);
    }
  }, [defaultDocumentLocationId, locationFilter]);

  useEffect(() => {
    if (locationFilter === 'all' || locations.length === 0) return;
    const hasSelectedLocation = locations.some((location) => location.id === locationFilter);
    if (!hasSelectedLocation) {
      setLocationFilter(defaultDocumentLocationId || 'all');
    }
  }, [defaultDocumentLocationId, locationFilter, locations]);

  const formatDashboardActivity = useCallback((activity) => {
    const entityKey = normalizeActivityEntityKey(activity.entity, activity.type);
    const typeKey = normalizeActivityType(activity.type, entityKey);
    const actionKey = String(activity.action || 'default').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
    const entityLabel = translateActivityValue(t, `dashboard.activityFeed.entities.${entityKey}`, translateActivityValue(t, 'dashboard.activityFeed.entities.generic', 'Activity'));
    const actionLabel = translateActivityValue(t, `dashboard.activityFeed.actions.${actionKey}`, translateActivityValue(t, 'dashboard.activityFeed.actions.default', 'updated'));
    const actorLabel = activity.actor_name || t('dashboard.activityFeed.unknownActor');
    const descriptionParts = [];

    if (activity.subject) {
      descriptionParts.push(activity.subject);
    }

    if (activity.details && activity.details !== activity.subject) {
      descriptionParts.push(activity.details);
    }

    descriptionParts.push(actorLabel);

    return {
      ...activity,
      type: typeKey,
      title: t('dashboard.activityFeed.title', { entity: entityLabel, action: actionLabel }),
      description: descriptionParts.filter(Boolean).join(' · ') || t('dashboard.activityFeed.noDetails'),
      time: formatRelativeActivityTime(activity.timestamp, locale),
    };
  }, [locale, t]);

  const recentActivities = useMemo(() => (
    recentActivityRecords.map(formatDashboardActivity)
  ), [formatDashboardActivity, recentActivityRecords]);
  
  // ============================================================================
  // DATA LOADING
  // ============================================================================
  
  const calculatePerformanceData = useCallback((summaryPayload) => {
    if (!summaryPayload) {
      setPerformanceData(null);
      return;
    }

    const monthTarget = 500000;
    const clientsTarget = 30;
    const servicesTarget = 20;

    setPerformanceData({
      salesTarget: {
        current: summaryPayload?.kpis?.revenue?.value || 0,
        target: monthTarget
      },
      clientsTarget: {
        current: Math.floor(Math.random() * 25) + 5,
        target: clientsTarget
      },
      servicesTarget: {
        current: Math.floor(Math.random() * 15) + 3,
        target: servicesTarget
      },
      performanceScore: Math.min(95, Math.floor(
        ((summaryPayload?.kpis?.revenue?.value || 0) / monthTarget) * 100
      )),
      kpis: {
        avgTicket: summaryPayload?.kpis?.ticket_medio?.value || 0,
        avgTicketChange: 5.2,
        conversionRate: 78,
        conversionChange: 2.1,
        satisfaction: 92,
        satisfactionChange: 1.5,
        productivity: '12/h',
        productivityChange: 8.3
      }
    });
  }, []);

  const loadDashboardData = useCallback(async () => {
    if (period === 'custom' && (!customStartDate || !customEndDate)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      resetFetchTelemetry();

      const [locationsRes, summaryRes, dashboardAlertsRes, salesRes, expensesRes, topRes, recentActivitiesRes, lowStockRes, servicesRes] = await Promise.all([
        safeRequest(() => settingsAPI.listActiveLocations(), { data: [] }, { telemetry: { module: 'dashboard', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getDashboardSummary(dashboardParams), { data: null }, { telemetry: { module: 'dashboard', endpoint: 'reports.summary', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getDashboardAlerts({ limit: 10 }), { data: { items: [], by_source_module: {} } }, { telemetry: { module: 'dashboard', endpoint: 'reports.dashboard_alerts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getSalesChart({ ...dashboardParams, group_by: 'day' }), { data: null }, { telemetry: { module: 'dashboard', endpoint: 'reports.sales_chart', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getExpensesChart(dashboardParams), { data: null }, { telemetry: { module: 'dashboard', endpoint: 'reports.expenses_chart', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getTopSales(dashboardParams), { data: null }, { telemetry: { module: 'dashboard', endpoint: 'reports.top_sales', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getRecentActivities({ ...dashboardParams, limit: 12 }), { data: { items: [] } }, { telemetry: { module: 'dashboard', endpoint: 'reports.recent_activities', onEvent: captureFetchTelemetry } }),
        safeRequest(() => inventoryAPI.getLowStock(), { data: [] }, { telemetry: { module: 'dashboard', endpoint: 'inventory.low_stock', onEvent: captureFetchTelemetry } }),
        safeRequest(() => servicesAPI.getSummary(), { data: {} }, { telemetry: { module: 'dashboard', endpoint: 'services.summary', onEvent: captureFetchTelemetry } })
      ]);

      const nextSummary = summaryRes.data || null;
      setLocations(locationsRes.data || []);
      setSummary(nextSummary);
      setDashboardAlerts(dashboardAlertsRes.data || { items: [], by_source_module: {}, diagnostics: null });
      setSalesChart(salesRes.data || null);
      setExpensesChart(expensesRes.data || null);
      setTopSales(topRes.data || null);
      setRecentActivityRecords(recentActivitiesRes.data?.items || []);
      setPendingTasks({
        lowStock: lowStockRes.data?.length || 0,
        services: servicesRes.data?.waiting || 0,
        quotations: servicesRes.data?.pending_approval || 0
      });
      calculatePerformanceData(nextSummary);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [calculatePerformanceData, captureFetchTelemetry, customEndDate, customStartDate, dashboardParams, period, resetFetchTelemetry, t]);

  useEffect(() => {
    const customRangeReady = period !== 'custom' || (customStartDate && customEndDate);
    if (canViewDashboard && customRangeReady) {
      loadDashboardData();
    }
  }, [canViewDashboard, customEndDate, customStartDate, loadDashboardData, period]);

  const handlePeriodChange = useCallback((value) => {
    setPeriod(value);
  }, []);
  
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    toast.success(t('dashboard.refreshed'));
  }, [loadDashboardData, t]);
  
  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  const salesChartData = useMemo(() => {
    if (!salesChart?.current) return [];
    return salesChart.current.map(d => ({
      date: d.date?.split('-').slice(1).join('/') || d.date,
      atual: d.revenue || 0,
      anterior: 0
    }));
  }, [salesChart]);
  
  const expensesDonutData = useMemo(() => {
    if (!expensesChart?.by_category) return [];
    return expensesChart.by_category.slice(0, 6).map((cat, idx) => ({
      name: cat.category || 'Sem categoria',
      value: cat.total,
      fill: CHART_COLORS[idx % CHART_COLORS.length]
    }));
  }, [expensesChart]);
  
  const salesInsightsData = useMemo(() => ({
    todaySales: summary?.kpis?.revenue?.value || 0,
    todayTarget: 50000,
    monthSales: summary?.kpis?.revenue?.value || 0,
    monthTarget: 500000,
    streak: 5,
    topSellers: topSales?.top_employees?.slice(0, 3).map(e => ({
      id: e.id,
      name: e.name,
      sales_count: e.sales,
      total: e.revenue
    })) || [],
    lastSale: {
      customer: 'Cliente Particular',
      amount: 12500,
      time: 'Há 5 min'
    },
    trend: summary?.kpis?.revenue?.change_pct || 0,
    achievements: [
      { type: 'streak_3', label: '3 Dias Seguidos', earned: true },
      { type: 'first_sale', label: 'Primeira Venda', earned: true }
    ]
  }), [summary, topSales]);
  const hasDashboardData = Boolean(
    summary?.kpis ||
    salesChart?.current?.length ||
    expensesChart?.by_category?.length ||
    topSales?.top_products?.length ||
    topSales?.top_employees?.length
  );
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (!canViewDashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center">
        <GlassCard className="p-12 text-center max-w-md">
          <XCircle className="h-16 w-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('dashboard.accessRestricted')}</h2>
          <p className="text-slate-600">
            {t('dashboard.accessRestrictedDescription')}
          </p>
        </GlassCard>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30" data-testid="dashboard-page">
      {/* ====================================================================== */}
      {/* HEADER */}
      {/* ====================================================================== */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/images/logo-cg.png" 
                alt="CG Business" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t('dashboard.title')}
                </h1>
                <p className="text-sm text-slate-500">
                  {t('dashboard.greeting', {
                    name: user?.name?.split(' ')[0] || user?.full_name?.split(' ')[0] || t('dashboard.defaultUserName')
                  })}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-end gap-3" data-testid="dashboard-header-filters">
              <LivePulse label={t('dashboard.live')} />
              
              <PeriodSelectControl
                period={period}
                onPeriodChange={handlePeriodChange}
                periodOptions={periodOptions}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomStartChange={setCustomStartDate}
                onCustomEndChange={setCustomEndDate}
                selectTestId="period-selector"
                customRangeTestId="dashboard-custom-period-range"
                customStartTestId="dashboard-custom-start-date-input"
                customEndTestId="dashboard-custom-end-date-input"
              />
              
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="w-44 bg-white border-slate-200" data-testid="location-filter">
                  <MapPin className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder={t('dashboard.allLocations')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('dashboard.allLocations')}</SelectItem>
                  {locations.filter(l => ['store', 'warehouse'].includes(l.type)).map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {locationFilter !== 'all' && defaultDocumentLocationId === locationFilter && defaultDocumentLocationName ? (
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="dashboard-default-location-badge">
                  {t('dashboard.defaultLocationBadge', { location: defaultDocumentLocationName })}
                </Badge>
              ) : null}
              
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleRefresh}
                className="bg-white"
                data-testid="refresh-btn"
              >
                <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* ====================================================================== */}
      {/* MAIN CONTENT */}
      {/* ====================================================================== */}
      <div className="p-6 space-y-6">
        <ModuleStateBoundary
          loading={loading}
          hasData={hasDashboardData}
          partialIssues={partialIssues}
          loadingTestId="dashboard-loading-state"
          partialIssuesTestId="dashboard-partial-issues"
          emptyState={(
            <EmptyStatePanel
              title={t('dashboard.emptyStateTitle')}
              description={t('dashboard.emptyStateDescription')}
              ctaLabel={t('dashboard.viewFullReport')}
              ctaTo="/reports"
              testId="dashboard-empty-state"
            />
          )}
        >
          <>
            {/* ================================================================ */}
            {/* KPI CARDS ROW */}
            {/* ================================================================ */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard
                title={t('dashboard.totalRevenue')}
                value={summary?.kpis?.revenue?.value || 0}
                change={summary?.kpis?.revenue?.change_pct}
                icon={DollarSign}
                color="green"
                subtitle={t('dashboard.previousPeriod')}
                onClick={() => navigate('/sales')}
                testId="dashboard-stat-total-revenue"
              />
              <StatCard
                title={t('dashboard.totalExpenses')}
                value={summary?.kpis?.expenses?.value || 0}
                change={summary?.kpis?.expenses?.change_pct}
                icon={Receipt}
                color="red"
                subtitle={t('dashboard.previousPeriod')}
                onClick={() => navigate('/expenses')}
                testId="dashboard-stat-total-expenses"
              />
              <StatCard
                title={t('dashboard.netResult')}
                value={summary?.kpis?.net_result?.value || 0}
                change={summary?.kpis?.net_result?.change_pct}
                icon={summary?.kpis?.net_result?.value >= 0 ? TrendingUp : TrendingDown}
                color={summary?.kpis?.net_result?.value >= 0 ? 'green' : 'red'}
                testId="dashboard-stat-net-result"
              />
              <StatCard
                title={t('dashboard.avgTicket')}
                value={summary?.kpis?.ticket_medio?.value || 0}
                icon={ShoppingCart}
                color="blue"
                subtitle={t('dashboard.salesCount', {
                  count: summary?.kpis?.ticket_medio?.sales_count || 0
                })}
                testId="dashboard-stat-avg-ticket"
              />
              <StatCard
                title={t('dashboard.cashDifference')}
                value={Math.abs(summary?.kpis?.cash_difference?.value || 0)}
                icon={Wallet}
                color={Math.abs(summary?.kpis?.cash_difference?.value || 0) < 100 ? 'green' : 'orange'}
                format="currency"
                onClick={() => navigate('/cash')}
                testId="dashboard-stat-cash-difference"
              />
              <StatCard
                title={t('dashboard.alerts')}
                value={summary?.kpis?.alerts_active || 0}
                icon={AlertTriangle}
                color={summary?.kpis?.alerts_active > 0 ? 'orange' : 'green'}
                format="number"
                onClick={() => navigate('/alerts')}
                testId="dashboard-stat-alerts"
              />
            </div>
            
            {/* ================================================================ */}
            {/* MAIN GRID - 3 COLUMNS */}
            {/* ================================================================ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* LEFT COLUMN - Sales Insights & Charts */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Sales Chart */}
                <GlassCard className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <Activity className="h-5 w-5 text-indigo-600" />
                        {t('dashboard.salesEvolution')}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {t('dashboard.performanceOverTime')}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                      {selectedPeriodLabel}
                    </Badge>
                  </div>
                  
                  {salesChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={salesChartData}>
                        <defs>
                          <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                          type="monotone" 
                          dataKey="atual" 
                          name={t('dashboard.salesLabel')}
                          stroke={COLORS.primary}
                          strokeWidth={3}
                          fill="url(#salesGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>{t('dashboard.noSalesInPeriod')}</p>
                      </div>
                    </div>
                  )}
                </GlassCard>
                
                {/* Two Column Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Expenses by Category */}
                  <GlassCard className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-rose-500" />
                        {t('dashboard.expensesByCategory')}
                      </h3>
                    </div>
                    
                    {expensesDonutData.length > 0 ? (
                      <div className="flex items-center">
                        <ResponsiveContainer width="50%" height={200}>
                          <RechartsPieChart>
                            <Pie
                              data={expensesDonutData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={80}
                              dataKey="value"
                            >
                              {expensesDonutData.map((entry) => (
                                <Cell key={`${entry.name}-${entry.fill}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                        <div className="w-1/2 space-y-2">
                          {expensesDonutData.slice(0, 5).map((cat) => (
                            <div key={`${cat.name}-${cat.value}`} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.fill }} />
                                <span className="truncate max-w-[80px] text-slate-600">{cat.name}</span>
                              </div>
                              <span className="font-medium text-slate-900">{formatCurrency(cat.value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-slate-400">
                        <p className="text-sm">{t('dashboard.noExpensesInPeriod')}</p>
                      </div>
                    )}
                  </GlassCard>
                  
                  {/* Top Products */}
                  <GlassCard className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Package className="h-5 w-5 text-cyan-500" />
                        {t('dashboard.topProducts')}
                      </h3>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')} data-testid="dashboard-top-products-view-all-button">
                        {t('dashboard.viewAll')}
                      </Button>
                    </div>
                    
                    {topSales?.top_products?.length > 0 ? (
                      <div className="space-y-3">
                        {topSales.top_products.slice(0, 5).map((product, idx) => (
                          <div key={product.id || product.sku || product.name} className="flex items-center gap-3">
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold",
                              idx === 0 && "bg-amber-100 text-amber-700",
                              idx === 1 && "bg-slate-100 text-slate-600",
                              idx === 2 && "bg-orange-100 text-orange-700",
                              idx > 2 && "bg-slate-50 text-slate-500"
                            )}>
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{product.name}</p>
                              <p className="text-xs text-slate-500">{t('dashboard.productsSold', { count: product.quantity })}</p>
                            </div>
                            <span className="font-semibold text-emerald-600 text-sm">
                              {formatCurrency(product.revenue)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-[180px] flex items-center justify-center text-slate-400">
                        <p className="text-sm">{t('dashboard.noSalesInPeriod')}</p>
                      </div>
                    )}
                  </GlassCard>
                </div>
                
                {/* Top Employees */}
                <GlassCard className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-500" />
                      {t('dashboard.topEmployees')}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} data-testid="dashboard-top-employees-report-button">
                      {t('dashboard.viewFullReport')}
                    </Button>
                  </div>
                  
                  {topSales?.top_employees?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {topSales.top_employees.slice(0, 6).map((emp, idx) => (
                        <div 
                          key={emp.id || emp.name}
                          className={cn(
                            "p-4 rounded-xl border transition-all hover:shadow-md",
                            idx === 0 && "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200",
                            idx === 1 && "bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200",
                            idx === 2 && "bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200",
                            idx > 2 && "bg-white border-slate-200"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center font-bold",
                              idx === 0 && "bg-amber-500 text-white",
                              idx === 1 && "bg-slate-400 text-white",
                              idx === 2 && "bg-orange-400 text-white",
                              idx > 2 && "bg-slate-200 text-slate-600"
                            )}>
                              {idx === 0 ? <Crown className="h-5 w-5" /> : idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate">{emp.name}</p>
                              <p className="text-xs text-slate-500">{t('dashboard.salesCount', { count: emp.sales })}</p>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-lg font-bold text-emerald-600">{formatCurrency(emp.revenue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-slate-400">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{t('dashboard.noTopEmployees')}</p>
                    </div>
                  )}
                </GlassCard>
              </div>
              
              {/* RIGHT COLUMN - Quick Actions & Activity */}
              <div className="space-y-6">
                
                {/* Quick Actions */}
                <GlassCard className="p-6">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                    <Zap className="h-5 w-5 text-amber-500" />
                    {t('dashboard.quickActions')}
                  </h3>
                  
                  <div className="space-y-2">
                    <QuickActionBtn 
                      icon={ShoppingCart} 
                      label={t('dashboard.newSale')} 
                      color="green"
                      onClick={() => navigate('/sales')}
                      testId="dashboard-quick-action-sale-button"
                    />
                    <QuickActionBtn 
                      icon={FileText} 
                      label={t('dashboard.newQuotation')} 
                      color="blue"
                      onClick={() => navigate('/documents')}
                      testId="dashboard-quick-action-quotation-button"
                    />
                    <QuickActionBtn 
                      icon={Users} 
                      label={t('dashboard.newClient')} 
                      color="purple"
                      onClick={() => navigate('/clients')}
                      testId="dashboard-quick-action-client-button"
                    />
                    <QuickActionBtn 
                      icon={Wrench} 
                      label={t('dashboard.newService')} 
                      color="orange"
                      onClick={() => navigate('/services')}
                      testId="dashboard-quick-action-service-button"
                    />
                    <QuickActionBtn 
                      icon={Package} 
                      label={t('dashboard.inventory')} 
                      color="pink"
                      badge={pendingTasks.lowStock > 0 ? t('dashboard.lowStockBadge', { count: pendingTasks.lowStock }) : null}
                      onClick={() => navigate('/inventory')}
                      testId="dashboard-quick-action-inventory-button"
                    />
                  </div>
                </GlassCard>
                
                {/* Pending Tasks */}
                {(pendingTasks.services > 0 || pendingTasks.quotations > 0 || pendingTasks.lowStock > 0) && (
                  <GlassCard className="p-6 border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/50">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      {t('dashboard.requiresAttention')}
                    </h3>
                    
                    <div className="space-y-3">
                      {pendingTasks.services > 0 && (
                        <button 
                          onClick={() => navigate('/services')}
                          className="w-full flex items-center justify-between p-3 rounded-lg bg-white border border-amber-200 hover:border-amber-300 transition-colors"
                          data-testid="dashboard-pending-services-button"
                        >
                          <div className="flex items-center gap-3">
                            <Wrench className="h-4 w-4 text-amber-600" />
                            <span className="text-sm font-medium">{t('dashboard.waitingServices')}</span>
                          </div>
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            {pendingTasks.services}
                          </Badge>
                        </button>
                      )}
                      
                      {pendingTasks.quotations > 0 && (
                        <button 
                          onClick={() => navigate('/documents')}
                          className="w-full flex items-center justify-between p-3 rounded-lg bg-white border border-blue-200 hover:border-blue-300 transition-colors"
                          data-testid="dashboard-pending-quotations-button"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium">{t('dashboard.pendingQuotations')}</span>
                          </div>
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                            {pendingTasks.quotations}
                          </Badge>
                        </button>
                      )}
                      
                      {pendingTasks.lowStock > 0 && (
                        <button 
                          onClick={() => navigate('/inventory')}
                          className="w-full flex items-center justify-between p-3 rounded-lg bg-white border border-red-200 hover:border-red-300 transition-colors"
                          data-testid="dashboard-low-stock-button"
                        >
                          <div className="flex items-center gap-3">
                            <Package className="h-4 w-4 text-red-600" />
                            <span className="text-sm font-medium">{t('dashboard.lowStock')}</span>
                          </div>
                          <Badge variant="destructive">
                            {pendingTasks.lowStock}
                          </Badge>
                        </button>
                      )}
                    </div>
                  </GlassCard>
                )}

                {/* Active Alerts Drill-down */}
                <GlassCard className="p-6 border-orange-200/70 bg-gradient-to-br from-orange-50/50 to-white" data-testid="dashboard-alerts-drilldown-card">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Bell className="h-5 w-5 text-orange-500" />
                        Alertas activos rastreáveis
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Total real: <span className="font-semibold text-slate-900" data-testid="dashboard-alerts-drilldown-total">{summary?.kpis?.alerts_active || 0}</span>
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate('/alerts')} data-testid="dashboard-alerts-open-central-button">
                      Abrir central
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4" data-testid="dashboard-alerts-module-breakdown">
                    {Object.entries(dashboardAlerts?.by_source_module || {}).length > 0 ? Object.entries(dashboardAlerts.by_source_module).map(([moduleKey, count]) => (
                      <Badge key={moduleKey} variant="outline" className="bg-white text-slate-700 border-slate-200">
                        {ALERT_SOURCE_LABELS[moduleKey] || moduleKey}: {count}
                      </Badge>
                    )) : (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Sem alertas activos</Badge>
                    )}
                  </div>

                  {dashboardAlerts?.items?.length > 0 ? (
                    <div className="space-y-3" data-testid="dashboard-alerts-drilldown-list">
                      {dashboardAlerts.items.map((alert) => (
                        <button
                          key={alert.id}
                          type="button"
                          onClick={() => navigate(alert.source_route || '/alerts')}
                          className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-orange-300 hover:shadow-sm transition-all"
                          data-testid={`dashboard-alert-row-${String(alert.id).replace(/[^a-zA-Z0-9-_]/g, '-')}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                                  {ALERT_SOURCE_LABELS[alert.source_module] || alert.source_module}
                                </Badge>
                                <Badge className={cn('border', ALERT_SEVERITY_CLASSES[alert.severity] || ALERT_SEVERITY_CLASSES.medium)}>
                                  {alert.severity}
                                </Badge>
                              </div>
                              <p className="text-sm font-medium text-slate-900">{alert.message}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                Registo: {alert.source_record_label || alert.source_record_id} · Regra: {alert.alert_type}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-xs text-slate-400">{formatDashboardDate(alert.created_at, locale)}</p>
                              <p className="text-xs text-orange-600 mt-1">Abrir origem</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm text-emerald-700" data-testid="dashboard-alerts-drilldown-empty-state">
                      Nenhum alerta activo real encontrado nas fontes integradas.
                    </div>
                  )}
                </GlassCard>
                
                {/* Recent Activity */}
                <GlassCard className="p-6" data-testid="dashboard-recent-activity-feed">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Activity className="h-5 w-5 text-blue-500" />
                      {t('dashboard.recentActivity')}
                    </h3>
                    <LivePulse label={t('dashboard.live')} />
                  </div>
                  
                  <ScrollArea className="h-[320px]" data-testid="dashboard-recent-activity-scrollarea">
                    <div className="space-y-1">
                      {recentActivities.length > 0 ? (
                        recentActivities.map((activity) => (
                          <ActivityItem key={getActivityReactKey(activity)} activity={activity} />
                        ))
                      ) : (
                        <div className="py-12 text-center text-slate-400">
                          <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t('dashboard.noRecentActivity')}</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </GlassCard>
                
                {/* Performance Score */}
                {performanceData && (
                  <GlassCard className="p-6 bg-gradient-to-br from-indigo-50/50 to-purple-50/50">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                      <Target className="h-5 w-5 text-indigo-500" />
                      {t('dashboard.performanceScore')}
                    </h3>
                    
                    <div className="flex items-center justify-center py-4">
                      <div className="relative">
                        <svg className="w-32 h-32 transform -rotate-90">
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="#e5e7eb"
                            strokeWidth="10"
                            fill="none"
                          />
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke={performanceData.performanceScore >= 80 ? COLORS.success : performanceData.performanceScore >= 60 ? COLORS.warning : COLORS.danger}
                            strokeWidth="10"
                            fill="none"
                            strokeDasharray={`${(performanceData.performanceScore / 100) * 351.86} 351.86`}
                            strokeLinecap="round"
                            className="transition-all duration-1000"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-bold text-slate-900">
                            {performanceData.performanceScore}
                          </span>
                          <span className="text-xs text-slate-500">{t('dashboard.points')}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 rounded-lg bg-white/60 border border-indigo-100">
                      <p className="text-center text-sm">
                        {performanceData.performanceScore >= 90 && (
                          <span className="text-emerald-600 font-medium flex items-center justify-center gap-1">
                            <Star className="h-4 w-4" /> {t('dashboard.excellentPerformance')}
                          </span>
                        )}
                        {performanceData.performanceScore >= 70 && performanceData.performanceScore < 90 && (
                          <span className="text-blue-600 font-medium">{t('dashboard.keepGoing')}</span>
                        )}
                        {performanceData.performanceScore < 70 && (
                          <span className="text-amber-600 font-medium">{t('dashboard.roomToImprove')}</span>
                        )}
                      </p>
                    </div>
                  </GlassCard>
                )}
              </div>
            </div>
            
            {/* ================================================================ */}
            {/* TOP EXPENSES TABLE */}
            {/* ================================================================ */}
            {expensesChart?.top_expenses?.length > 0 && (
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-rose-500" />
                    {t('dashboard.topExpenses')}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/expenses')} data-testid="dashboard-top-expenses-view-all-button">
                    {t('dashboard.viewAll')}
                  </Button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50/80 rounded-lg">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 rounded-l-lg">{t('labels.code')}</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('labels.description')}</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('labels.category')}</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">{t('labels.amount')}</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 rounded-r-lg">{t('labels.date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expensesChart.top_expenses.map((expense) => (
                        <tr key={expense.id || expense.expense_code || expense.description} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                          <td className="py-3 px-4">
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono">
                              {expense.expense_code}
                            </code>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700">{expense.description}</td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-xs">
                              {expense.expense_category_name || 'N/A'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-rose-600">
                            {formatCurrency(expense.amount)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {formatDashboardDate(expense.created_at, locale)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            )}
          </>
        </ModuleStateBoundary>
      </div>
    </div>
  );
}
